import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import dayjs from 'dayjs';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { DataGrid, SelectColumn } from 'react-data-grid';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import ExportExcel from '../../Components/ExportExcel';
import ImportExcel from '../../Components/ImportExcel';
import {
  FilterContext,
  rowKeyGetter,
  FilterRenderer,
  inputStopPropagation,
  selectStopPropagation,
  autoFocusAndSelect,
  handleCopy,
  renderTextEditor,
  mergeEditedGridRows,
} from '../../Components/ReactDataGridProps';
import ManageReportDataApi from '../../Api/ApiManageReportData';


function EmployeeTable({
  name,
  rows,
  companies,
  organisations,
  orgLength,
  activeFilter,
  selectedDates,
  groupCheck,
  roleCheck,
  getStateData,
  onClickedAdd,
  onClickedDelete,
  onUpdateDropDownData,
  groupRoleCheck,
  resetReportingState,
}) {
  const [tableRows, setTableRows] = useState([]);
  const [filters, setFilters] = useState({
    AccountNumber: '',
    AccountName: '',
    DimCompanyKey: 'All',
    DimOrganisationKey: 'All',
    enabled: false,
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortColumns, setSortColumns] = useState([]);
  const [frozenColumns, setFrozenColumns] = useState([]);

  const getComparator = (sortColumn) => {
    switch (sortColumn) {
      case 'Group':
      case 'EmployeeFirstName':
      case 'EmployeeLastName':
      case 'EmployeeInitials':
      case 'EmployeeRole':
      case 'Email':
        return (a, b) => {
          return a[sortColumn]?.localeCompare(b[sortColumn]);
        };

      case 'TerminationDate':
        return (a, b) => {
          return new Date(a[sortColumn]) - new Date(b[sortColumn]);
        };
      
      case 'FteEquivalent':
      case 'TargetUtilisationRation':
        return (a, b) => {
          return a[sortColumn] - b[sortColumn];
        };
  
      case 'DimCompanyKey':
        return (a, b) => {
          const aName = getCompanyNameById(a[sortColumn]);
          const bName = getCompanyNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };
  
      case 'DimOrganisationKey':
        return (a, b) => {
          const aName = getOrganisationNameById(a[sortColumn]);
          const bName = getOrganisationNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };
  
      default:
        throw new Error(`unsupported sortColumn: "${sortColumn}"`);
    }
  }

  useEffect(() => {
    setTableRows(
      rows.sort((a, b) =>
        b.DimDateKey === a.DimDateKey
          ? a.EmployeeFirstName?.localeCompare(b.EmployeeFirstName)
          : b.DimDateKey - a.DimDateKey,
      ),
    );

    return () => {
      setTableRows([]);
    };
  }, [rows]);

  const getCompanyNameById = (id) => {
    if (!id) return '';
    const company = companies?.find(company => company.id === id);
    return company ? company.CompanyName : '';
  };

  const getOrganisationNameById = (id) => {
    if (!id) return '';
    const organisation = organisations?.find(organisation => organisation.id === id);
    return organisation ? organisation.OrgName : '';
  };

  const handleClickRow = (ids) => {
    setSelectedRow(null);
    setSelectedIds(ids);
  };

  const handleCellClick = (rowData) => {
    setSelectedRow(rowData.row);
  };

  const handlePaste = ({
    sourceColumnKey,
    sourceRow,
    targetColumnKey,
    targetRow,
  }) => {
    const incompatibleColumns = [
      'DimCompanyKey',
      'DimOrganisationKey',
      'IsActive',
      'IsAdminStaff',
      'TerminationDate',
    ];
    if (
      incompatibleColumns.includes(targetColumnKey) ||
      incompatibleColumns.includes(sourceColumnKey)
    ) {
      return targetRow;
    }

    return { ...targetRow, [targetColumnKey]: sourceRow[sourceColumnKey] };
  };

  const toggleFilters = () => {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  };

  const handleClickDelete = () => {
    if (selectedIds.size > 0) {
      onClickedDelete(name, Array.from(selectedIds));
      setSelectedIds(new Set());
      setSelectedRow(null);
    } else {
      if (selectedRow) {
        let rowIndexes = [selectedRow.id];
        onClickedDelete(name, rowIndexes);
        setSelectedRow(null);
      }
    }
  };

  const handleGroupRoleCheck = (event) => {
    groupRoleCheck(event.target.name, event.target.checked);
  };

  const handleSelectFrozenColumns = (event, name) => {
    event.stopPropagation();
    event.preventDefault();
    const currentIndex = frozenColumns.indexOf(name);
    const newColumns = [...frozenColumns];

    if (currentIndex === -1) {
      newColumns.push(name);
    } else {
      newColumns.splice(currentIndex, 1);
    }
    setFrozenColumns(newColumns);
  };

  const handleExcelImport = (updated_data) => {
    // check import data is valid
    const validData = updated_data.every(item => {
      return item.hasOwnProperty('EmployeeId') && item.hasOwnProperty('EmployeeName') && item.hasOwnProperty('EmployeeFirstName')
        && item.hasOwnProperty('EmployeeLastName') && item.hasOwnProperty('EmployeeInitials') && item.hasOwnProperty('EmployeeRole')
        && item.hasOwnProperty('FteEquivalent') && item.hasOwnProperty('TargetUtilisationRation');
    });
    if (!validData) {
      toast.warning('Please insert the valid data. Data must contain EmployeeId, EmployeeName, EmployeeFirstName, EmployeeLastName, EmployeeInitials, EmployeeRole, FteEquivalent, and TargetUtilisationRation.');
      return;
    }

    ManageReportDataApi.importDataFromExcel(
      name,
      updated_data,
      selectedDates,
    ).then((res) => {
      if (res.status === 200) {
        getStateData(res.data);
      } else toast.error(res.message);
    })
    .catch((error) => toast.error(error.message));
  };

  const handleGridRowsUpdated = (updatedRows, field) => {
    let updated_data = {
      rowIds: [updatedRows[field.indexes[0]].id],
      updated: {
        [field.column.key]: updatedRows[field.indexes[0]][field.column.key],
      },
    };

    if (updated_data.updated[field.column.key] === '') {
      toast.warning('Please insert the column value.');
      return;
    }

    if (
      field.column.key === 'FteEquivalent' ||
      field.column.key === 'TargetUtilisationRation'
    ) {
      if (isNaN(updated_data.updated[field.column.key])) {
        toast.warning('Please insert the number value.');
        return;
      }
    }

    if (selectedIds.size > 0) {
      updated_data.rowIds = [
        ...updated_data.rowIds,
        ...Array.from(selectedIds),
      ];      
    }

    updatedRows.map((row) => {
      if (updated_data.rowIds.includes(row.id))
        row[field.column.key] =
          updatedRows[field.indexes[0]][field.column.key];
      return row;
    });

    setTableRows((currentRows) =>
      mergeEditedGridRows(
        currentRows,
        updatedRows,
        updated_data.rowIds,
        updated_data.updated,
      ),
    );
    ManageReportDataApi.updateTemplate(name, updated_data)
      .then((res) => {
        if (res.status === 200) {
          onUpdateDropDownData(name);
          resetReportingState();
        } else {
          toast.error(res.message);
        }
      })
      .catch((error) => toast.error(error.message));
  };

  const employeeColumns = useMemo(() => {
    let employee_columns = [
      SelectColumn,
      {
        key: 'Group',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Group</b>
            {frozenColumns.indexOf('Group') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Group')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Group')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'Group')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Group')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 110,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.Group}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Group: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('Group') !== -1 ? true : false,
        renderEditCell: renderTextEditor,
      },
      {
        key: 'EmployeeFirstName',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>First Name</b>
            {frozenColumns.indexOf('EmployeeFirstName') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'EmployeeFirstName')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'EmployeeFirstName')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find(
              (column) => column.columnKey === 'EmployeeFirstName',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'EmployeeFirstName',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 140,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.EmployeeFirstName}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    EmployeeFirstName: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen:
          frozenColumns.indexOf('EmployeeFirstName') !== -1 ? true : false,
        renderEditCell: renderTextEditor,
      },
      {
        key: 'EmployeeLastName',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Last Name</b>
            {frozenColumns.indexOf('EmployeeLastName') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'EmployeeLastName')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'EmployeeLastName')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find(
              (column) => column.columnKey === 'EmployeeLastName',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'EmployeeLastName',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 135,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.EmployeeLastName}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    EmployeeLastName: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('EmployeeLastName') !== -1 ? true : false,
        renderEditCell: renderTextEditor,
      },
      {
        key: 'EmployeeInitials',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Middle Initial</b>
            {frozenColumns.indexOf('EmployeeInitials') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'EmployeeInitials')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'EmployeeInitials')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find(
              (column) => column.columnKey === 'EmployeeInitials',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'EmployeeInitials',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 155,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.EmployeeInitials}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    EmployeeInitials: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('EmployeeInitials') !== -1 ? true : false,
        renderEditCell: renderTextEditor,
      },
      {
        key: 'EmployeeRole',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Employee Role</b>
            {frozenColumns.indexOf('EmployeeRole') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'EmployeeRole')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'EmployeeRole')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'EmployeeRole')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'EmployeeRole',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 165,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.EmployeeRole}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    EmployeeRole: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('EmployeeRole') !== -1 ? true : false,
        renderEditCell: renderTextEditor,
      },
      {
        key: 'Email',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Email</b>
            {frozenColumns.indexOf('Email') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Email')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Email')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'Email')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Email')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        resizable: true,
        width: 200,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.Email}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Email: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('Email') !== -1 ? true : false,
        renderEditCell: renderTextEditor,
      },
      {
        key: 'FteEquivalent',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>FTE Value</b>
            {frozenColumns.indexOf('FteEquivalent') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'FteEquivalent')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'FteEquivalent')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'Email')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Email')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 125,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.FteEquivalent}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    FteEquivalent: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('FteEquivalent') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.FteEquivalent}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                FteEquivalent: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'TargetUtilisationRation',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Target Utilisation Ration</b>
            {frozenColumns.indexOf('TargetUtilisationRation') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'TargetUtilisationRation')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'TargetUtilisationRation')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find(
              (column) => column.columnKey === 'TargetUtilisationRation',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'TargetUtilisationRation',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 240,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.TargetUtilisationRation}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    TargetUtilisationRation: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen:
          frozenColumns.indexOf('TargetUtilisationRation') !== -1
            ? true
            : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.TargetUtilisationRation}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                TargetUtilisationRation: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'IsActive',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Active</b>
            {frozenColumns.indexOf('IsActive') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'IsActive')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'IsActive')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
          </div>
        ),
        frozen: frozenColumns.indexOf('IsActive') !== -1 ? true : false,
        renderCell: (props) => (
          <div style={{ textAlign: 'center' }}>
            <Checkbox
              checked={props.row.IsActive}
              onChange={(e) => {
                props.onRowChange({
                  ...props.row,
                  IsActive: e.target.checked,
                });
              }}
              color="success"
            />
          </div>
        ),
      },
      {
        key: 'IsAdminStaff',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Admin</b>
            {frozenColumns.indexOf('IsAdminStaff') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'IsAdminStaff')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'IsAdminStaff')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
          </div>
        ),
        frozen: frozenColumns.indexOf('IsAdminStaff') !== -1 ? true : false,
        renderCell: (props) => (
          <div style={{ textAlign: 'center' }}>
            <Checkbox
              checked={props.row.IsAdminStaff}
              onChange={(e) => {
                props.onRowChange({
                  ...props.row,
                  IsAdminStaff: e.target.checked,
                });
              }}
              color="success"
            />
          </div>
        ),
      },
      {
        key: 'TerminationDate',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Termination Date</b>
            {frozenColumns.indexOf('TerminationDate') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'TerminationDate')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'TerminationDate')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
          </div>
        ),
        sortable: true,
        width: 200,
        frozen: frozenColumns.indexOf('TerminationDate') !== -1 ? true : false,
        renderCell: (props) => {
          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={['DatePicker']}>
                <DatePicker
                  sx={{
                    minWidth: '149px !important',
                    marginTop: '-8px !important',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                  variant="standard"
                  value={dayjs(props.row.TerminationDate)}
                  onChange={(newValue) => {
                    props.onRowChange(
                      { ...props.row, TerminationDate: newValue.$d },
                      true,
                    );
                  }}
                />
              </DemoContainer>
            </LocalizationProvider>
          );
        },
      },
    ];

    if (orgLength.length > 1) {
      employee_columns.splice(1, 0, {
        key: 'DimOrganisationKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Department</b>
            {frozenColumns.indexOf('DimOrganisationKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimOrganisationKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimOrganisationKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimOrganisationKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimOrganisationKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 205,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimOrganisationKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimOrganisationKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimOrganisationKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {organisations &&
                  organisations.map(
                    (cur) =>
                      p.row.DimCompanyKey === cur.DimCompanyKey &&
                      cur.IsActive && (
                        <option key={cur.id} value={cur.id}>
                          {cur.OrgName}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimOrganisationKey != null &&
                props.row.DimOrganisationKey
              }
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimOrganisationKey: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {organisations &&
                organisations.map(
                  (cur) =>
                    props.row.DimCompanyKey === cur.DimCompanyKey &&
                    cur.IsActive && (
                      <MenuItem key={cur.id} value={cur.id}>
                        {cur.OrgName}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      });
    }

    if (companies.length > 1) {
      employee_columns.splice(1, 0, {
        key: 'DimCompanyKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Company</b>
            {frozenColumns.indexOf('DimCompanyKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimCompanyKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimCompanyKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimCompanyKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimCompanyKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 205,
        resizable: true,
        sortable: true,
        frozen: frozenColumns.indexOf('DimCompanyKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimCompanyKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimCompanyKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {companies &&
                  companies.map(
                    (cur) =>
                      cur.IsActive && (
                        <option key={cur.id} value={cur.id}>
                          {cur.CompanyName}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimCompanyKey != null && props.row.DimCompanyKey}
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimCompanyKey: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {companies &&
                companies.map(
                  (cur) =>
                    cur.IsActive && (
                      <MenuItem key={cur.id} value={cur.id}>
                        {cur.CompanyName}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      });
    }

    let frozenCols = employee_columns.filter((col) => col.frozen === true);
    let unfrozenCols = employee_columns.filter((col) => col.frozen === false);
    employee_columns = [...frozenCols, ...unfrozenCols];

    return employee_columns;
    // eslint-disable-next-line
  }, [
    tableRows,
    sortColumns,
    frozenColumns,
    companies,
    organisations,
    orgLength,
    handleSelectFrozenColumns,
  ]);
  
  const filteredRows = useMemo(() => {
    return tableRows?.filter((r) => {
      const isActiveEmployees = r.IsActive && !r.TerminationDate;

      return (
        (!activeFilter || isActiveEmployees) &&
        (filters.Group
          ? r.Group?.toLowerCase().includes(filters.Group.toLowerCase())
          : true) &&
        (filters.EmployeeFirstName
          ? r.EmployeeFirstName?.toLowerCase().includes(
              filters.EmployeeFirstName.toLowerCase(),
            )
          : true) &&
        (filters.EmployeeLastName
          ? r.EmployeeLastName?.toLowerCase().includes(
              filters.EmployeeLastName.toLowerCase(),
            )
          : true) &&
        (filters.EmployeeInitials
          ? r.EmployeeInitials?.toLowerCase().includes(
              filters.EmployeeInitials.toLowerCase(),
            )
          : true) &&
        (filters.EmployeeRole
          ? r.EmployeeRole?.toLowerCase().includes(
              filters.EmployeeRole.toLowerCase(),
            )
          : true) &&
        (filters.Email
          ? r.Email?.toLowerCase().includes(filters.Email.toLowerCase())
          : true) &&
        (filters.FteEquivalent
          ? r.FteEquivalent?.toString().includes(
              filters.FteEquivalent.toString(),
            )
          : true) &&
        (filters.TargetUtilisationRation
          ? r.TargetUtilisationRation?.toString().includes(
              filters.TargetUtilisationRation.toString(),
            )
          : true) &&
        (filters.DimCompanyKey !== 'All'
          ? r.DimCompanyKey === filters.DimCompanyKey
          : true) &&
        (filters.DimOrganisationKey !== 'All'
          ? r.DimOrganisationKey === filters.DimOrganisationKey
          : true)
      );
    });
  }, [tableRows, filters, activeFilter, selectedDates]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);
  
  const exportValue = useMemo(() => {
    let export_value = {
      Client: {
        columns: [],
        data: [],
      },
      Company: {
        columns: [],
        data: [],
      },
      Organisation: {
        columns: [],
        data: [],
      },
    };

    export_value.Client.columns = [
      { title: 'DimCompanyKey', width: { wpx: 80 } },
      { title: 'DimOrganisationKey', width: { wpx: 80 } },
      { title: 'Group', width: { wpx: 80 } },
      { title: 'EmployeeFirstName', width: { wpx: 80 } },
      { title: 'EmployeeLastName', width: { wpx: 80 } },
      { title: 'EmployeeInitials', width: { wpx: 80 } },
      { title: 'EmployeeRole', width: { wpx: 80 } },
      { title: 'Email', width: { wpx: 80 } },
      { title: 'FteEquivalent', width: { wpx: 80 } },
      { title: 'TargetUtilisationRation', width: { wpx: 80 } },
      //{ title: 'HoursPerPayPeriod', width: { wpx: 80 } },
      { title: 'IsActive', width: { wpx: 80 } },
      { title: 'IsAdminStaff', width: { wpx: 80 } },
      { title: 'TerminationDate', width: { wpx: 80 } },
    ];
    tableRows.forEach((data) => {
      export_value.Client.data.push([
        {
          value: data.DimCompanyKey ? Number(data.DimCompanyKey) : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimOrganisationKey ? Number(data.DimOrganisationKey) : -1,
          style: { numFmt: '0' },
        },
        { value: data.Group ? data.Group : '' },
        { value: data.EmployeeFirstName ? data.EmployeeFirstName : '' },
        { value: data.EmployeeLastName ? data.EmployeeLastName : '' },
        { value: data.EmployeeInitials ? data.EmployeeInitials : '' },
        { value: data.EmployeeRole ? data.EmployeeRole : '' },
        { value: data.Email ? data.Email : '' },
        {
          value: data.FteEquivalent ? Number(data.FteEquivalent) : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.TargetUtilisationRation
            ? Number(data.TargetUtilisationRation)
            : 0,
          style: { numFmt: '0.00' },
        },
        // {
        //   value: data.HoursPerPayPeriod ? Number(data.HoursPerPayPeriod) : 0,
        //   style: { numFmt: '0.00' },
        // },
        { value: data.IsActive ? 'true' : 'false' },
        { value: data.IsAdminStaff ? 'true' : 'false' },
        { value: data.TerminationDate ? data.TerminationDate : '' },
      ]);
    });

    export_value.Company.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'CompanyName', width: { wpx: 80 } },
    ];
    companies &&
      companies.forEach((cur) => {
        export_value.Company.data.push([
          { value: cur.id, style: { numFmt: '0' } },
          { value: cur.CompanyName },
        ]);
      });

    export_value.Organisation.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'OrgName', width: { wpx: 80 } },
    ];
    organisations &&
      organisations.forEach((cur) => {
        export_value.Organisation.data.push([
          { value: cur.id, style: { numFmt: '0' } },
          { value: cur.OrgName },
        ]);
      });

    return export_value;
  }, [tableRows, companies, organisations]);

  return (
    <div>
      <Box display="flex" alignItems="center" gap={2}>
        <div style={{ flex: 1 }} />        
        <Tooltip title="Group" placement="top">
          <FormControlLabel
            control={
              <Checkbox
                checked={groupCheck}
                onChange={handleGroupRoleCheck}
                name="groupCheck"
                color="success"
              />
            }
            label="If checked Group will refresh from Accounting API"
            sx={{
              '& .MuiFormControlLabel-label': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          />
        </Tooltip>
        <Tooltip title="Role" placement="top">
          <FormControlLabel
            control={
              <Checkbox
                checked={roleCheck}
                onChange={handleGroupRoleCheck}
                name="roleCheck"
                color="success"
              />
            }
            label="If checked Role will refresh from Accounting API"
          />
        </Tooltip>
      
        <ImportExcel onImport={handleExcelImport} />
        <ExportExcel filename="Employee" value={exportValue} />
        <IconButton aria-label="Add" onClick={() => onClickedAdd(name)}>
          <AddIcon />
        </IconButton>
        <IconButton aria-label="Search" onClick={toggleFilters}>
          <SearchIcon />
        </IconButton>
        <IconButton aria-label="Delete" onClick={handleClickDelete}>
          <DeleteIcon />
        </IconButton>
      </Box>
      <FilterContext.Provider value={filters}>
        <DataGrid
          rowKeyGetter={rowKeyGetter}
          className={
            filters.enabled
              ? 'filter-container fill-grid rdg-light'
              : 'fill-grid rdg-light'
          }
          headerRowHeight={filters.enabled ? 70 : 50}
          columns={employeeColumns}
          rows={sortedRows}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns}
          selectedRows={selectedIds}
          onSelectedRowsChange={handleClickRow}
          onRowsChange={handleGridRowsUpdated}
          onCellClick={handleCellClick}
          onCopy={handleCopy}
          onPaste={handlePaste}
          rowHeight={50}
          enableVirtualization={true}
          style={{ maxHeight: window.innerHeight - 180 - (23 * (selectedDates.length - 1)) }}
        />
      </FilterContext.Provider>
    </div>
  );
}

EmployeeTable.propTypes = {
  name: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  companies: PropTypes.array.isRequired,
  organisations: PropTypes.array.isRequired,  
  orgLength: PropTypes.array.isRequired,
  activeFilter: PropTypes.bool.isRequired,
  selectedDates: PropTypes.array.isRequired,
  groupCheck: PropTypes.bool.isRequired,
  roleCheck: PropTypes.bool.isRequired,
  onClickedAdd: PropTypes.func.isRequired,
  onClickedDelete: PropTypes.func.isRequired,
  getStateData: PropTypes.func.isRequired,
  onClickedUpdate: PropTypes.func.isRequired,
  onUpdateDropDownData: PropTypes.func.isRequired,
  groupRoleCheck: PropTypes.func.isRequired,
};

export default EmployeeTable;
